import { discoveryApiRef, fetchApiRef, useApi } from "@backstage/core-plugin-api";
import {
	AVD_PLUGIN_ID,
	BUNDLE_RECLAMATIONS_MINE_PATH,
	type BundleReclamationRecord,
	isIsoTimestamp,
	type MyBundleReclamationsResponse,
} from "@internal/backstage-plugin-avd-common";
import { useEffect, useState } from "react";

import { deleteOnText } from "./myDesktop";

// The owner's view of the reclamation log: docs/infra/bundle-contract.md § Reclamation log.

/** The absent line for a reclaimed desktop: when, after how long, and that nothing is left (ADR-0024). */
export function reclaimText(row: Pick<BundleReclamationRecord, "recordedAt" | "daysIdle">): string {
	const days = `${row.daysIdle} day${row.daysIdle === 1 ? "" : "s"}`;
	return (
		`Your desktop was deleted for inactivity on ${deleteOnText(row.recordedAt)}, after ${days} ` +
		"without use. Nothing was backed up, and it can't be recovered — set up a fresh one below."
	);
}

/** The reclaim whose run also wrote the absent row (`absentRunUrl`); re-read when that run changes. */
export function useMyReclamation(
	bundleType: string,
	absentRunUrl: string | undefined,
): BundleReclamationRecord | undefined {
	const discoveryApi = useApi(discoveryApiRef);
	const fetchApi = useApi(fetchApiRef);
	const [latest, setLatest] = useState<BundleReclamationRecord>();

	useEffect(() => {
		let active = true;
		setLatest(undefined);
		if (!absentRunUrl) return undefined;
		const read = async () => {
			try {
				const baseUrl = await discoveryApi.getBaseUrl(AVD_PLUGIN_ID);
				const res = await fetchApi.fetch(`${baseUrl}${BUNDLE_RECLAMATIONS_MINE_PATH}`);
				if (!res.ok) return;
				const body = (await res.json()) as Partial<MyBundleReclamationsResponse>;
				if (!active || !Array.isArray(body.items)) return;
				// The route answers for every type, newest first.
				setLatest(
					body.items.find(
						(r) =>
							r.bundleType === bundleType &&
							r.enforced === true &&
							isIsoTimestamp(r.recordedAt) &&
							Number.isInteger(r.daysIdle),
					),
				);
			} catch {
				// Fail open: no reclaim line.
			}
		};
		void read();
		return () => {
			active = false;
		};
	}, [discoveryApi, fetchApi, bundleType, absentRunUrl]);

	return latest?.runUrl === absentRunUrl ? latest : undefined;
}
